// @flow
import * as React from 'react';
import Attributes from 'esm-web-pro/components/layout/sidePanel/Attributes';
import {
    defineMessages, FormattedMessage, injectIntl, IntlShape,
} from 'react-intl';
import attributeMessages from 'esm-web-pro/messages/attributeMessages';
import point11Messages from 'esm-web-pro/messages/points11Messages';
import { point12Messages } from 'esm-web-pro/messages/points12Messages';
import point14Messages from 'esm-web-pro/messages/points14Messages';
import type { FiberDetailedRepresentation } from 'esm-web-pro/types/networks';


type Props = {
    fiber: FiberDetailedRepresentation,
    url: string,
    intl: IntlShape,
    onView: Function
}

const FIBER_CONTAINER_TYPE = 'cable';

const msg = defineMessages({
    damagedTrue: {
        id: 'fiber-damaged-true',
        defaultMessage: 'True',
    },
    damagedFalse: {
        id: 'fiber-damaged-false',
        defaultMessage: 'False',
    },
    start: {
        id: 'fiber-start-container-point',
        defaultMessage: 'Start',
    },
    end: {
        id: 'fiber-end-container-point',
        defaultMessage: 'End',
    },
    sequenceNumber: {
        id: 'fiber-sequence-number',
        defaultMessage: 'Sequence number',
    },
    isDamaged: {
        id: 'fiber-is-damaged',
        defaultMessage: 'Damaged',
    },
    cable: {
        id: 'cable-fiber-container',
        defaultMessage: 'Cable',
    },
});

const FiberAttributes = (props: Props) => {
    const {
        fiber, url, intl: { formatMessage }, onView,
    } = props;
    const {
        start_container_point: startContainerPoint,
        end_container_point: endContainerPoint,
        start_termination_point: startTerminationPoint,
        end_termination_point: endTerminationPoint,
        container: {
            start_container_point: cableStartContainerPoint,
            end_container_point: cableEndContainerPoint,
            length,
        },
        is_damaged: isDamaged,
        model,
        ref,
    } = fiber;

    const getAttributeFields = (point, messages) => (
        {
            value: `${formatMessage(messages[point.type])} ${
                point.type === 'pop' && point.alias
                    ? point.alias
                    : point.sequence
            }`,
            link: `${url.split('/layer')[0]}/layer/${point.type}/${point.identity}`,
        }
    );

    const getAttribute = (terminationPoint, containerPoint, cableContainerPoint, label) => {
        const pointsAttributes = [getAttributeFields(cableContainerPoint, point11Messages)];

        if (containerPoint) {
            pointsAttributes.push(getAttributeFields(containerPoint, point12Messages));
        }
        if (terminationPoint) {
            pointsAttributes.push(getAttributeFields(terminationPoint, point14Messages));
        }

        return { label, multiValue: pointsAttributes, separator: ', ' };
    };

    const attributes = [
        {
            label: formatMessage(attributeMessages.ref),
            value: ref,
            onClick: onView,
        },
        {
            label: formatMessage(attributeMessages.model),
            value: model,
            link: model ? `${url}/model/${model.code}`.replace('//', '/') : '',
        },
        {
            label: formatMessage(attributeMessages.container),
            value: `${formatMessage(msg.cable)} ${fiber.container.ref}`,
            link: `${url.split('/layer')[0]}/layer/${FIBER_CONTAINER_TYPE}/${fiber.container.identity}`,
        },
        getAttribute(startTerminationPoint, startContainerPoint, cableStartContainerPoint, formatMessage(msg.start)),
        getAttribute(endTerminationPoint, endContainerPoint, cableEndContainerPoint, formatMessage(msg.end)),
        {
            label: <FormattedMessage id="cable-attributes-length" defaultMessage="Length" />,
            value: <FormattedMessage
                id="cable-attributes-length-value"
                defaultMessage="{value} m."
                values={{ value: length }}
            />,
        },
        {
            label: formatMessage(msg.isDamaged),
            value: isDamaged ? formatMessage(msg.damagedTrue) : formatMessage(msg.damagedFalse),
        },
    ];

    return <Attributes attributes={attributes} />;
};

export default injectIntl(FiberAttributes);
